<template>
  <div class="peerReviewsProfessorCommentsTable">
    <div v-if="editable" class="aboveProfessorTableHeaderButtons">
      <AddButton @clickEvent="addEntry"></AddButton>
    </div>

    <div class="professorTable">
      <ValidationObserver v-slot="{ invalid }">
        <b-table :items="tableData" :fields="columns" striped sticky-header>
          <template #cell(date)="data">
            {{ returnAsFormatedMoment(data.value) }}
          </template>

          <template #cell(content)="data">
            <ValidationProvider
              v-if="tableData[data.index].isEdit"
              v-slot="{ errors }"
              :vid="'content' + data.index"
              name="content"
              rules="required|max:256"
            >
              <div v-if="tableData[data.index].isEdit" class="inputCenterAlignment">
                <TextArea
                  :value="tableData[data.index].content"
                  :rows="textAreaRows"
                  :max-rows="textAreaMaxRows"
                  :style-type="textAreaStyle"
                  @input="tableData[data.index].content = $event"
                ></TextArea>
              </div>

              <span class="professorTableErrorMessages">{{ errors[0] }}</span>
            </ValidationProvider>

            <span v-else>
              <span class="textAreaSpan">{{ data.value }}</span>
            </span>
          </template>

          <template #cell(fullName)="data">
            {{ data.value }}
          </template>

          <template #cell(visibility)="data">
            <ValidationProvider
              v-if="tableData[data.index].isEdit"
              v-slot="{ errors }"
              :vid="'visibility' + data.index"
              name="visibility"
              rules="required"
            >
              <div v-if="tableData[data.index].isEdit" class="inputCenterAlignment">
                <b-form-select
                  id="formSelect"
                  v-model="tableData[data.index].visibility"
                  :options="tableData[data.index].options"
                ></b-form-select>
              </div>

              <span class="professorTableErrorMessages">{{ errors[0] }}</span>
            </ValidationProvider>

            <span v-else>
              <span class="textAreaSpan">{{ $t(mapVisibilityIntToString(data.value)) }}</span>
            </span>
          </template>

          <template v-if="editable" #cell(edit)="data">
            <div v-if="!tableData[data.index].readOnly">
              <span class="tableButtons">
                <span v-if="currentlyNotEditingOrDeletingEntry(data)">
                  <EditButton class="professorTableButtons" @clickEvent="editRowHandler(data)"></EditButton>
                </span>

                <span v-if="!currentlyNotEditingOrDeletingEntry(data)">
                  <ConfirmButton class="professorTableButtons" :disabled="invalid" @clickEvent="confirmEditOrDelete(data)"></ConfirmButton>
                </span>

                <span v-if="currentlyNotEditingOrDeletingEntry(data)">
                  <DeleteButton @clickEvent="promptToConfirmDelete(data)"></DeleteButton>
                </span>

                <span v-if="!currentlyNotEditingOrDeletingEntry(data)">
                  <CancelButton @clickEvent="stopEditingOrDeleting(data)"></CancelButton>
                </span>
              </span>
            </div>
          </template>
        </b-table>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
import AddButton from '@/components/util/Buttons/TableButtons/AddButton'
import CancelButton from '@/components/util/Buttons/TableButtons/CancelButton'
import ConfirmButton from '@/components/util/Buttons/TableButtons/ConfirmButton'
import DeleteButton from '@/components/util/Buttons/TableButtons/DeleteButton'
import EditButton from '@/components/util/Buttons/TableButtons/EditButton'

import TextArea from '@/components/util/Inputs/TextArea'

import { generalHelperMethodsMixin } from '../../../mixins/generalHelperMethodsMixin'
import { editableTableMixin } from '../../../mixins/editableTableMixin'
import { filtersMixin } from '../../../mixins/filtersMixin'

import { peerReviewsMixin } from './PeerReviewsMixin'
import mapGettersMixin from '../../../mixins/mapGettersMixin'
import { processPromisesMixin } from '../../../mixins/processPromisesMixin'

import axios from '../../../../axios'

export default {
  name: 'ProfessorPeerReviewsComments',
  components: { AddButton, CancelButton, ConfirmButton, DeleteButton, EditButton, TextArea },
  mixins: [generalHelperMethodsMixin, editableTableMixin, filtersMixin, peerReviewsMixin, mapGettersMixin, processPromisesMixin],

  props: {
    userId: {
      type: [Number, String],
      required: true
    },

    week: {
      type: [Number, String],
      default: null
    },

    editable: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      localeConsts: {
        peerReviewsDateColumn: 'WORKREPORTS.DATE',
        peerReviewscontentColumn: 'WORKREPORTS.CONTENT',
        peerReviewsFromColumn: 'WORKREPORTS.FROM',
        peerReviewsVisibilityColumn: 'WORKREPORTS.VISIBILITY'
      },

      textAreaRows: 5,
      textAreaMaxRows: 12,
      textAreaStyle: 'table',

      fetchedData: [],
      tableData: [],
      idToName: {}
    }
  },

  computed: {
    columns() {
      return [
        {
          key: 'date',
          label: this.$i18n.t(this.localeConsts.peerReviewsDateColumn),
          thClass: 'text-center professorTableThStyle',
          tdClass: 'text-center'
        },

        {
          key: 'content',
          label: this.$i18n.t(this.localeConsts.peerReviewscontentColumn),
          thClass: 'text-center professorTableThStyle ',
          tdClass: 'text-center contentTextAreaStyle'
        },

        {
          key: 'fullName',
          label: this.$i18n.t(this.localeConsts.peerReviewsFromColumn),
          thClass: 'text-center professorTableThStyle',
          tdClass: 'text-center'
        },

        {
          key: 'visibility',
          label: this.$i18n.t(this.localeConsts.peerReviewsVisibilityColumn),
          thClass: 'text-center professorTableThStyle',
          tdClass: 'text-center'
        },

        { key: 'edit', label: '', thClass: 'text-center professorTableThStyle', tdClass: 'text-center text-nowrap' }
      ]
    }
  },

  watch: {
    week: {
      deep: true,
      handler() {
        this.getCommentsAndFilterByWeekVisibilityAndTargetId()
      }
    }
  },

  mounted() {
    this.getCommentsAndFilterByWeekVisibilityAndTargetId()
  },

  methods: {
    addEntry() {
      // insert a dummy entry
      this.tableData.unshift({
        date: new Date(),
        content: '',
        fullName: this.idToName[this.getUserId],
        options: [
          { text: this.$i18n.t('VISIBILITY.ONLY_ME'), value: 0 },
          { text: this.$i18n.t('VISIBILITY.SUPERVISORS_ONLY'), value: 1 },
          { text: this.$i18n.t('VISIBILITY.SUPERVISORS_AND_STUDENTS'), value: 2 }
        ],
        visibility: 0,
        isEdit: true,
        isDelete: false
      })

      this.sortByDateDesc({ data: this.tableData, dateColumn: 'date' })
    },

    async confirmEditOrDelete(entry) {
      if (this.tableData[entry.index].isDelete) {
        this.deleteRow(entry.index)
      } else if (this.isNewEntry(entry)) {
        const responseProjectId = await this.getProjectId
        const entryIndex = entry.index

        const newEntry = {
          author: this.getUserId,
          content: this.tableData[entryIndex].content,
          date: this.tableData[entryIndex].date,
          visibility: this.tableData[entryIndex].visibility,
          project: responseProjectId,
          target: this.userId
        }
        axios
          .post('peer-review-comments/', newEntry)
          .then((response) => {
            this.tableData[entryIndex].id = response.data.id
            this.fetchedData.push({ ...newEntry, id: response.data.id })
          })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.error('Error saving peer review comment:', error)
            this.tableData.splice(entryIndex, 1)
          })
      } else {
        const patchEntry = {
          content: this.tableData[entry.index].content,
          visibility: this.tableData[entry.index].visibility
        }
        const patchResponse = await axios.patch(`peer-review-comments/${this.tableData[entry.index].id}/`, patchEntry)
        // is necessary in order to avoid bugs when canceling the editing
        const indexOfPatchedEntry = this.findFetchedDataIndexById(patchResponse.data.id)
        this.fetchedData[indexOfPatchedEntry] = patchEntry
      }

      this.stopEditingOrDeleting(entry, true)
      this.sortByDateDesc({ data: this.tableData, dateColumn: 'date' })
    },

    deleteRow(index) {
      axios.delete(`peer-review-comments/${this.tableData[index].id}/`)
      this.tableData.splice(index, 1)
      this.sortByDateDesc({ data: this.tableData, dateColumn: 'date' })
    },

    /*table helper methods*/

    stopEditingOrDeleting(entry, isConfirm = false) {
      // if it is called by cancel then it should refresh the data by calling filterOnWeek
      this.tableData[entry.index].isDelete = false
      this.tableData[entry.index].isEdit = false

      if (!isConfirm) {
        this.getCommentsAndFilterByWeekVisibilityAndTargetId()
      }
    }
  }
}
</script>

<style lang="scss">
$th-color: #451a6e;
$th-background-color: #c1afd1;
$error-text-color: red;

/*General table styling*/

.table .professorTableThStyle {
  color: $th-color !important;
  background-color: $th-background-color !important;
}

/*professor table buttons*/
.aboveProfessorTableHeaderButtons {
  display: flex;
  justify-content: space-between;
}

.tableButtons {
  display: flex;
}

.professorTableButtons {
  margin-right: 10px;
}

/*inputs*/
.inputCenterAlignment {
  display: grid;
  place-items: center;
}

.contentTextAreaStyle {
  max-width: 400px;
}
/*formSelect styling*/
#formSelect {
  background: white;
}

/*textArea styling*/

.textAreaSpan {
  line-break: anywhere;
  white-space: pre-wrap;
}

/*input error messages*/

.professorTableErrorMessages {
  color: $error-text-color;
  font-size: 12px;
}
</style>
